import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography,
  Stack,
  Divider,
} from "@mui/material";
import React from "react";
import { useTranslation } from "react-i18next";
import { Hangar } from "../../types";

interface AssignAircraftDialogProps {
  open: boolean;
  onClose: () => void;
  aircraftTail: string;
  aircraftModel: string;
  aircraftWingspan?: number;
  aircraftLength?: number;
  availableHangars: Hangar[];
  onAssign: (hangarId: string) => void;
  isAssigning?: boolean;
}

/**
 * Dialog for assigning aircraft from holding area to a hangar/ramp
 * Shows list of available locations with size warnings
 */
export const AssignAircraftDialog: React.FC<AssignAircraftDialogProps> = ({
  open,
  onClose,
  aircraftTail,
  aircraftModel,
  aircraftWingspan,
  aircraftLength,
  availableHangars,
  onAssign,
  isAssigning = false,
}) => {
  const { t } = useTranslation();
  const [selectedHangarId, setSelectedHangarId] = React.useState<string>("");

  // Reset selection when dialog opens
  React.useEffect(() => {
    if (open) {
      setSelectedHangarId("");
    }
  }, [open]);

  const handleAssign = () => {
    onAssign(selectedHangarId);
  };

  // Helper to check if aircraft is too large for hangar (width or length)
  const isTooLarge = (hangar: Hangar): boolean => {
    if (!aircraftWingspan && !aircraftLength) return false;

    // Check wingspan vs usable width
    const widthIssue = aircraftWingspan && hangar.width
      ? aircraftWingspan > (hangar.width - (hangar.left_door || 0) - (hangar.right_door || 0))
      : false;

    // Check aircraft length vs hangar depth
    const depthIssue = aircraftLength && hangar.depth
      ? aircraftLength > hangar.depth
      : false;

    return widthIssue || depthIssue;
  };

  // Group hangars and ramps with memoization
  const { hangars, ramps } = React.useMemo(() => {
    return availableHangars.reduce(
      (acc, h) => {
        if (h.id.includes("ramp")) {
          acc.ramps.push(h);
        } else {
          acc.hangars.push(h);
        }
        return acc;
      },
      { hangars: [] as Hangar[], ramps: [] as Hangar[] }
    );
  }, [availableHangars]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {t("screens.multiHangar.assignAircraftDialog.title")}
        <Typography variant="body2" color="text.secondary">
          {aircraftTail} • {aircraftModel}
        </Typography>
      </DialogTitle>

      <DialogContent>
        <FormControl component="fieldset" fullWidth>
          <RadioGroup
            value={selectedHangarId}
            onChange={(e) => setSelectedHangarId(e.target.value)}
          >
            {/* Hangars Section */}
            {hangars.length > 0 && (
              <>
                <Typography variant="subtitle2" color="text.secondary" sx={{ ml: 1, mb: 0.5 }}>
                  {t("screens.multiHangar.assignAircraftDialog.sections.hangars")}
                </Typography>
                {hangars.map((hangar) => {
                  const tooLarge = isTooLarge(hangar);
                  return (
                    <FormControlLabel
                      key={hangar.id}
                      value={hangar.id}
                      control={<Radio />}
                      label={
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Typography>{hangar.name}</Typography>
                          {tooLarge && (
                            <Stack direction="row" spacing={0.5} alignItems="center">
                              <WarningAmberIcon sx={{ fontSize: 16, color: "warning.main" }} />
                              <Typography variant="caption" color="warning.main">
                                {t("screens.multiHangar.assignAircraftDialog.messages.mayBeTooSmall")}
                              </Typography>
                            </Stack>
                          )}
                        </Stack>
                      }
                    />
                  );
                })}
              </>
            )}

            {/* Ramps Section */}
            {ramps.length > 0 && (
              <>
                <Divider sx={{ my: 1.5 }} />
                <Typography variant="subtitle2" color="text.secondary" sx={{ ml: 1, mb: 0.5 }}>
                  {t("screens.multiHangar.assignAircraftDialog.sections.ramps")}
                </Typography>
                {ramps.map((ramp) => (
                  <FormControlLabel
                    key={ramp.id}
                    value={ramp.id}
                    control={<Radio />}
                    label={ramp.name}
                  />
                ))}
              </>
            )}

            {/* Empty state - no locations available */}
            {hangars.length === 0 && ramps.length === 0 && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ textAlign: "center", py: 3 }}
              >
                {t("screens.multiHangar.assignAircraftDialog.messages.noLocations")}
              </Typography>
            )}
          </RadioGroup>
        </FormControl>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={isAssigning}>
          {t("screens.multiHangar.assignAircraftDialog.buttons.cancel")}
        </Button>
        <Button
          onClick={handleAssign}
          variant="contained"
          disabled={!selectedHangarId || isAssigning}
        >
          {isAssigning ? t("screens.multiHangar.assignAircraftDialog.buttons.assigning") : t("screens.multiHangar.assignAircraftDialog.buttons.assign")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
